import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LogisticsCalculationPublicDto } from '@magimp/shared';
import { Button } from '../../../ui/index.js';
import { DocumentSheet } from '../sheet/DocumentSheet.js';
import { parsePublicLogisticsCalculationToken } from './public-api.js';
import { usePublicLogisticsCalculation } from './use-public-logistics-calculation.js';

export function PublicLogisticsCalculationPage() {
  const { t } = useTranslation('logisticsCalculationsPublic');
  const [token] = useState(() =>
    typeof window === 'undefined'
      ? null
      : parsePublicLogisticsCalculationToken(window.location.hash),
  );
  const state = usePublicLogisticsCalculation(token);

  useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex,nofollow';
    const referrer = document.createElement('meta');
    referrer.name = 'referrer';
    referrer.content = 'no-referrer';
    document.head.append(robots, referrer);
    return () => {
      robots.remove();
      referrer.remove();
    };
  }, []);

  if (state.status === 'loading') {
    return <main className="rl-public-state">{t('loading')}</main>;
  }
  if (state.status === 'notFound' || !token) {
    return (
      <main className="rl-public-state" data-testid="rl-public-not-found">
        <section>
          <h1>{t('notFound.title')}</h1>
          <p>{t('notFound.body')}</p>
        </section>
      </main>
    );
  }
  return <PublicDocument dto={state.dto} />;
}

function PublicDocument({ dto }: { dto: LogisticsCalculationPublicDto }) {
  const { t } = useTranslation('logisticsCalculationsPublic');
  return (
    <main className="doc-page" data-testid="rl-public-sheet">
      <div className="doc-toolbar">
        <div className="doc-toolbar-actions">
          {/* WHY the browser's own print dialog: the page is already the A4
              document, so its print stylesheet produces the PDF a customer
              would otherwise ask a server to render. */}
          <Button
            kind="primary"
            size="sm"
            data-testid="rl-public-download-pdf"
            onClick={() => window.print()}
          >
            {t('downloadPdf')}
          </Button>
        </div>
      </div>
      <DocumentSheet dto={dto} publicCode={dto.publicCode} />
    </main>
  );
}
